/**
 * @description Reusable confirmation modal following Headless UI + Untitled UI patterns.
 * Replaces window.confirm() with a proper animated dialog.
 *
 * Key features:
 * - Headless UI Dialog with Transition animations (backdrop blur, scale)
 * - Configurable icon, colors, title, message, button text
 * - Loading state with spinner on action button
 * - Prevents close during loading
 *
 * @dependencies
 * - @headlessui/react: Dialog, Transition
 *
 * @usage
 * <ConfirmActionModal
 *   open={showConfirm}
 *   onClose={() => setShowConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Invoice"
 *   message="Are you sure? This cannot be undone."
 *   confirmText="Delete"
 *   variant="danger"
 * />
 */

import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle, Pause, Play, Info } from 'lucide-react';

const VARIANTS = {
  danger: {
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonBg: 'bg-red-600 hover:bg-red-500',
    Icon: AlertTriangle
  },
  warning: {
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    buttonBg: 'bg-yellow-600 hover:bg-yellow-500',
    Icon: Pause
  },
  success: {
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    buttonBg: 'bg-green-600 hover:bg-green-500',
    Icon: Play
  },
  info: {
    iconBg: 'bg-brand/10',
    iconColor: 'text-brand',
    buttonBg: 'bg-brand hover:bg-brand-hover',
    Icon: Info
  }
};

export default function ConfirmActionModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  icon: CustomIcon = null,
  loading: externalLoading = null
}) {
  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = externalLoading !== null ? externalLoading : internalLoading;

  const config = VARIANTS[variant] || VARIANTS.danger;
  const IconComponent = CustomIcon || config.Icon;

  const handleConfirm = async () => {
    try {
      setInternalLoading(true);
      await onConfirm();
    } catch (err) {
      console.error('Confirm action failed:', err);
    } finally {
      setInternalLoading(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={() => { if (!isLoading) onClose(); }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-md">
                {/* Header */}
                <div className="px-6 py-4 border-b bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${config.iconBg}`}>
                      <IconComponent className={`w-5 h-5 ${config.iconColor}`} />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                      {title}
                    </Dialog.Title>
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                  <p className="text-sm text-gray-600">{message}</p>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                  <button
                    onClick={onClose}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {cancelText}
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={isLoading}
                    className={`px-4 py-2 text-sm rounded-lg font-medium text-white disabled:opacity-50 transition-colors ${config.buttonBg}`}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </span>
                    ) : confirmText}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
